/**
 * Shared template for the four product-intent SEO landing pages (#353, FR-15.7) —
 * `/online-jeopardy`, `/jeopardy-game-maker`, `/multiplayer-jeopardy`,
 * `/custom-jeopardy-games`. One component parameterized by the route's
 * {@link SeoLandingContent} model, so the pages stay visually consistent with `/` (they
 * reuse the shipped `.landing-*` vocabulary + the four new `.seo-*` classes) and never
 * duplicate each other's copy (D353.5/D353.8).
 *
 * The `path` prop selects the content model AND the head model — the prerender renders each
 * route element directly (no `Routes` match), so the page can't read the path from
 * `useParams`; it's passed explicitly, exactly like {@link ./TriviaSet}'s `slug`. SSR-safe
 * (no `window`/timers at render top-level) so it bakes into crawlable HTML with a
 * self-referencing canonical (FR-15.2/§16). The single CTA targets `/demo` — the
 * zero-commitment create/join picker — and stays above the fold (D353.7). "Jeopardy-style"
 * copy is descriptive nominative use (FR-9.11, D353.1); every page carries the
 * {@link ../seo/trademark}.SEO_DISCLAIMER note.
 */

import { Link } from 'react-router-dom';
import { LandingHeader } from '../ui/landing/LandingHeader';
import { BoardVisual } from '../ui/landing/BoardVisual';
import { useDocumentHead } from '../seo/useDocumentHead';
import { headModelFor } from '../seo/route-seo';
import { SEO_LANDINGS, SEO_LANDING_BY_PATH } from '../seo/seo-landings';
import { SEO_DISCLAIMER } from '../seo/trademark';

/** A cross-link target: the sibling landing pages (minus self) plus the `/trivia` hub. */
function crossLinks(currentPath: string): { to: string; label: string }[] {
  const siblings = SEO_LANDINGS.filter((c) => c.path !== currentPath).map((c) => ({
    to: c.path,
    label: c.crossLinkLabel,
  }));
  return [...siblings, { to: '/trivia', label: 'Browse ready-to-play trivia' }];
}

export function SeoLanding({ path }: { path: string }): React.JSX.Element {
  useDocumentHead(headModelFor(path));
  const content = SEO_LANDING_BY_PATH[path];
  if (!content) {
    // A routing/prerender bug — a page element was mounted for a path with no model.
    throw new Error(`SeoLanding: no content model for "${path}"`);
  }
  const links = crossLinks(path);

  return (
    <div className="landing" data-testid="seo-landing">
      <LandingHeader />

      <header className="landing-wrap landing-hero">
        <div className="landing-hero__copy">
          <span className="landing-eyebrow">{content.eyebrow}</span>
          <h1 className="landing-hero__title">
            {content.heading.lead}
            <span className="landing-amber">{content.heading.amber}</span>
            {content.heading.tail}
          </h1>
          <p className="landing-hero__lead">{content.lead}</p>

          {content.flow && (
            <ul className="seo-flow" data-testid="seo-flow">
              {content.flow.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
          )}

          <div className="landing-cta-row landing-cta-row--stack">
            <Link
              className="btn btn--primary landing-cta landing-cta--demo"
              to="/demo"
              data-testid="seo-cta-primary"
            >
              {content.cta} →
            </Link>
            <Link
              className="btn btn--ghost landing-cta"
              to="/player"
              data-testid="seo-cta-join"
            >
              Join a Game
            </Link>
          </div>
          <p className="landing-subnote">
            Free to try · No download · Play on any device
          </p>
        </div>
        <BoardVisual />
      </header>

      <section className="landing-wrap landing-section" data-testid="seo-explain">
        <div className="landing-sec-head">
          <span className="landing-eyebrow">How it works</span>
          <h2>{content.explain.head}</h2>
          <p>{content.explain.body}</p>
        </div>

        {content.examples && (
          <div className="seo-examples" data-testid="seo-examples">
            {content.examples.map((set) => (
              <div className="seo-example-card" key={set.group}>
                <h3>{set.group}</h3>
                <ul>
                  {set.topics.map((topic) => (
                    <li key={topic}>{topic}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <section
        className="landing-wrap landing-section"
        data-testid="seo-crosslinks-section"
      >
        <div className="landing-sec-head">
          <span className="landing-eyebrow">Explore</span>
          <h2>Other ways to play</h2>
        </div>
        <div className="seo-crosslinks">
          {links.map((link) => (
            <Link key={link.to} to={link.to}>
              {link.label}
            </Link>
          ))}
        </div>
      </section>

      <section
        className="landing-wrap landing-section landing-why"
        data-testid="seo-cta-section"
      >
        <div className="landing-sec-head">
          <h2>Ready to build your game?</h2>
        </div>
        <div className="landing-cta-row">
          <Link
            className="btn btn--primary landing-cta"
            to="/demo"
            data-testid="seo-cta-bottom"
          >
            {content.cta} →
          </Link>
        </div>
        <p className="seo-disclaimer">{SEO_DISCLAIMER}</p>
      </section>
    </div>
  );
}
